import { isPlatformBrowser } from '@angular/common';
import type { OnDestroy } from '@angular/core';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { LocalStorage } from '@freelancer/local-storage';
import { Location } from '@freelancer/location';
import { Pwa } from '@freelancer/pwa';
import { isDefined } from '@freelancer/utils';
import { Subscription, take } from 'rxjs';
import { TrackingConsentStatus } from './interface';

/**
 * A service which fetches tracking consent from users,
 * ie. whether they have allowed us, or third parties, to track them.
 *
 * Currently, this determines consent based on platform, a specialised
 * `no_third_party_tracking` query parameter, or local storage (for
 * pixels where consent mode is supported) for third-party tracking.
 */
@Injectable({
  providedIn: 'root',
})
export class TrackingConsent implements OnDestroy {
  isDenied: boolean;
  private subscriptions = new Subscription();

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private localStorage: LocalStorage,
    private location: Location,
    private pwa: Pwa,
  ) {}

  /**
   * Get the user's current authorization status for third-party tracking.
   * eg. Facebook pixel, Google Analytics
   *
   * Bing and Google support consent mode.
   */
  async getThirdPartyStatus(
    isConsentModeSupported: boolean = false,
  ): Promise<TrackingConsentStatus> {
    // If explicitly disabled via query param, deny.
    // Also save state so if the query param disappears in later navigations,
    // the user's choice to deny takes priority over the current query param.
    this.isDenied =
      this.isDenied ||
      !!new URL(this.location.href).searchParams.get('no_third_party_tracking');
    if (this.isDenied) {
      return TrackingConsentStatus.DENIED;
    }

    if (this.pwa.isNative()) {
      // On native platforms, we set tracking as denied by default.
      // This could use a native API like iOS App Tracking Transparency,
      // but most users deny those anyway so it's probably a waste of time.
      return TrackingConsentStatus.DENIED;
    }

    if (isConsentModeSupported && isPlatformBrowser(this.platformId)) {
      return new Promise<TrackingConsentStatus>(resolve => {
        this.subscriptions.add(
          this.localStorage
            .get('trackingConsent', true)
            .pipe(take(1))
            .subscribe(trackingConsent => {
              if (isDefined(trackingConsent)) {
                resolve(
                  trackingConsent.consent
                    ? TrackingConsentStatus.AUTHORIZED
                    : TrackingConsentStatus.DECLINED,
                );
              }

              // Default to not determined if no consent has been set.
              resolve(TrackingConsentStatus.NOT_DETERMINED);
            }),
        );
      });
    }

    // Default to authorized for everything else.
    return TrackingConsentStatus.AUTHORIZED;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
